import { useCallback, useState } from "react";
import axios from "axios";

import { generateUploadUrl, confirmUpload } from "api/endpoints/media";

// F2 — useMediaUpload. Implements the backend's 3-step upload, shared by the
// rich-text editor (F3) and the future featured-image picker (F4/F5).
//
//   1. Request a presigned URL  (via api/client — JWT attached).
//   2. PUT the raw bytes to blob storage (DIRECT — no JWT, no API base URL).
//   3. Confirm the upload       (via api/client — JWT attached).
//
// upload(file, { altText, folderPath, visibility }) resolves to the confirmed
// MediaAssetResponse (callers need file_url / media_asset_id). `visibility`
// defaults to "public"; pass "private" for gated assets (e.g. whitepapers).
export default function useMediaUpload({ websiteId } = {}) {
  const [status, setStatus] = useState("idle"); // idle | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);

  const upload = useCallback(
    async (file, { altText, folderPath, visibility = "public" } = {}) => {
      setStatus("uploading");
      setProgress(0);
      setError(null);
      try {
        // Step 1 — presigned URL.
        const presign = await generateUploadUrl(websiteId, {
          originalFilename: file.name,
          fileSize: file.size,
          mimeType: file.type,
          visibility,
          folderPath,
        });

        // Step 2 — raw bytes straight to blob storage. This must bypass
        // api/client: no Authorization header, no base URL, no JSON encoding.
        await axios.request({
          url: presign.upload_url,
          method: presign.method || "PUT",
          data: file,
          headers: { ...(presign.required_headers || {}) },
          transformRequest: [(data) => data],
          onUploadProgress: (event) => {
            if (event.total) {
              setProgress(Math.round((event.loaded / event.total) * 100));
            }
          },
        });

        // Step 3 — confirm.
        const asset = await confirmUpload(websiteId, presign.media_asset_id, { altText });
        setProgress(100);
        setStatus("done");
        return asset;
      } catch (err) {
        setStatus("error");
        setError(err);
        throw err;
      }
    },
    [websiteId]
  );

  return { upload, progress, status, error };
}
